import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Phone, Loader2, Star } from "lucide-react";
import { TableSessionBanner } from "./TableSessionBanner";
import { TableSessionOrdersModal } from "./TableSessionOrdersModal";
import { BillRequestedScreen } from "./BillRequestedScreen";

export function TableSessionModal({
  open,
  onOpenChange,
  tableName,
  tableNumber,
  onStartSession,
  onViewOnly,
  isLoading,
  themeColor = "#f97316"
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [guests, setGuests] = useState("");
  const [error, setError] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setPhone(""); setGuests(""); setError(""); setMarketingConsent(false); }
  }, [open]);

  const handleViewOnly = () => {
    onViewOnly?.();
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Por favor, introduce tu nombre"); return; }
    if (!phone.trim()) { setError("Por favor, introduce tu teléfono"); return; }
    if (phone.replace(/\D/g, "").length < 6) { setError("Teléfono no válido"); return; }
    const guestCount = parseInt(guests, 10);
    if (!guests.trim() || isNaN(guestCount) || guestCount < 1) {
      setError("Cantidad de personas debe ser >= 1");
      return;
    }
    const errorMsg = await onStartSession({
      name: name.trim(),
      phone: phone.trim(),
      guests: guestCount,
      marketingConsent: marketingConsent
    });
    if (errorMsg) setError(errorMsg);
  };

  const tableNum = String(tableNumber || "—").padStart(2, "0");
  const customTableName = tableName?.trim();
  const hasCustomTableName = customTableName && !/^MESA\\s*0*\\d+$/i.test(customTableName);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onViewOnly?.(); onOpenChange(isOpen); }}>
      <DialogContent className="max-w-sm bg-white dark:bg-zinc-900 p-0 overflow-hidden" data-testid="table-session-modal">

        {/* Hero */}
        <div className="flex flex-col items-center justify-center pt-8 pb-5 px-6" style={{ backgroundColor: themeColor }}>
          {!hasCustomTableName && <p className="text-white/70 text-xs font-semibold uppercase tracking-[0.2em] mb-1">MESA</p>}
          <p className="text-white font-black leading-none" style={{ fontSize: hasCustomTableName ? "3rem" : "4.5rem" }}>{hasCustomTableName ? customTableName : tableNum}</p>
          <p className="text-white/90 font-semibold text-sm mt-2">Haz tu pedido desde tu móvil</p>
        </div>

        <div className="px-5 pt-4 pb-5 space-y-4 overflow-y-auto" style={{ maxHeight: "68vh" }}>

          {/* Value proposition */}
          <div className="space-y-2">
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              Haz tu pedido directamente desde tu teléfono sin esperar.
            </p>
            <ul className="space-y-1 pl-1">
              {[
                "Acumulas puntos de fidelidad",
                "Obtienes descuento en tu próxima visita",
                "Tu pedido llega más rápido",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span style={{ color: themeColor }} className="font-bold text-base leading-none">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label htmlFor="session-name" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Nombre <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="session-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre"
                    className="pl-10 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder:text-gray-400 border-gray-300 dark:border-gray-600"
                    autoComplete="name"
                    data-testid="session-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="session-phone" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Teléfono <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="session-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="612 345 678"
                    className="pl-10 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder:text-gray-400 border-gray-300 dark:border-gray-600"
                    type="tel"
                    autoComplete="tel"
                    data-testid="session-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="session-guests" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Cantidad de personas <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="session-guests"
                    type="number"
                    min="1"
                    value={guests}
                    onChange={(e) => setGuests(e.target.value)}
                    placeholder="Ej: 4"
                    className="pl-10 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder:text-gray-400 border-gray-300 dark:border-gray-600"
                    data-testid="session-guests-input"
                  />
                </div>
              </div>
            </div>

            {/* Loyalty block */}
            <div className="rounded-xl px-4 py-3 bg-amber-50 dark:bg-amber-900/20 space-y-1">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 flex-shrink-0 text-amber-500" />
                <p className="text-sm font-bold text-amber-700 dark:text-amber-300">Gana dinero con tus pedidos</p>
              </div>
              <p className="text-xs text-amber-600 dark:text-amber-400 pl-6 leading-relaxed">
                Cada compra acumula puntos que puedes usar como descuento.
              </p>
            </div>

            {/* Marketing consent checkbox */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <input
                id="marketing-consent"
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-1 w-4 h-4 cursor-pointer accent-blue-600"
                data-testid="marketing-consent-checkbox"
              />
              <label htmlFor="marketing-consent" className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed cursor-pointer flex-1">
                Quiero recibir promociones y novedades de este restaurante por WhatsApp. Puedo darme de baja en cualquier momento.
              </label>
            </div>

            {error && (
              <p className="text-sm text-red-500 font-medium bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg" data-testid="session-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 text-white font-bold text-base rounded-xl"
              style={{ backgroundColor: themeColor }}
              data-testid="start-session-btn"
            >
              {isLoading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Iniciando...</>
                : "Continuar y acumular puntos"
              }
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handleViewOnly}
              className="w-full h-10 text-sm font-medium rounded-xl border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
              data-testid="view-only-btn"
            >
              Solo ver el menú
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
